import _extends from "@babel/runtime/helpers/extends";
import React from 'react';
import styled, { css, useTheme } from 'styled-components';
import * as RadixCheckbox from '@radix-ui/react-checkbox';
import { IconCheck } from '@lmn/lastminute-react-icons/lastminute-icons-solid';
export var Checkbox = function Checkbox(_ref) {
  var checkbox = _ref.checkbox,
    disabled = _ref.disabled,
    isChecked = _ref.isChecked,
    indeterminate = _ref.indeterminate,
    error = _ref.error,
    onChange = _ref.onChange,
    labelPosition = _ref.labelPosition,
    icon = _ref.icon,
    secondaryLabel = _ref.secondaryLabel,
    _ref$secondaryLabelPo = _ref.secondaryLabelPosition,
    secondaryLabelPosition = _ref$secondaryLabelPo === void 0 ? 'SAME_LINE' : _ref$secondaryLabelPo,
    _ref$hoverBackground = _ref.hoverBackground,
    hoverBackground = _ref$hoverBackground === void 0 ? true : _ref$hoverBackground,
    padding = _ref.padding,
    isOnHover = _ref.isOnHover,
    className = _ref.className,
    value = _ref.value,
    name = _ref.name,
    required = _ref.required,
    fullWidth = _ref.fullWidth;
  var dsTheme = /*#__PURE__*/useTheme();
  var onCheckedChange = function onCheckedChange(checked) {
    onChange(checked);
  };
  var getIconColor = function getIconColor() {
    if (disabled) return dsTheme.kallisto.color.icon.static.iconDisabled;
    if (error) return dsTheme.kallisto.color.icon.static.iconError;
    return dsTheme.kallisto.color.icon.static.iconInverse;
  };
  var iconColor = getIconColor();
  var getCheckedState = function getCheckedState() {
    if (indeterminate) return 'indeterminate';
    return !!isChecked;
  };
  var CheckboxComponent = /*#__PURE__*/React.createElement(CheckboxSquare, _extends({
    id: checkbox.id,
    checked: getCheckedState(),
    disabled: !!disabled,
    $error: !!error,
    onCheckedChange: onCheckedChange,
    onClick: function onClick(e) {
      return e.stopPropagation();
    },
    "aria-label": checkbox.label
  }, value && {
    value: value
  }, name && {
    name: name
  }, required && {
    required: required
  }), /*#__PURE__*/React.createElement(CheckboxIndicator, null, indeterminate ? /*#__PURE__*/React.createElement(IndeterminateDash, {
    $color: iconColor
  }) : /*#__PURE__*/React.createElement(IconCheck, {
    color: iconColor,
    size: 12
  })));
  var isSecondLine = secondaryLabelPosition === 'SECOND_LINE' && !!secondaryLabel;
  if (isSecondLine) {
    if (checkbox.label || icon) {
      return /*#__PURE__*/React.createElement(Label, {
        $disabled: !!disabled,
        $error: !!error,
        $position: labelPosition,
        $hoverBackground: hoverBackground,
        $isOnHover: isOnHover,
        className: className,
        $padding: padding,
        onClick: function onClick(e) {
          return e.stopPropagation();
        },
        $fullWidth: fullWidth,
        htmlFor: checkbox.id
      }, CheckboxComponent, /*#__PURE__*/React.createElement(SecondaryLabelColumn, {
        $fullWidth: fullWidth,
        $position: labelPosition
      }, checkbox.label && /*#__PURE__*/React.createElement(LabelText, null, checkbox.label), /*#__PURE__*/React.createElement(SecondaryLabel, {
        $disabled: disabled,
        $secondaryLine: true
      }, secondaryLabel)), icon && /*#__PURE__*/React.createElement(LabelIcon, null, icon));
    }
    return /*#__PURE__*/React.createElement(SecondaryLabelStack, {
      className: className
    }, CheckboxComponent, /*#__PURE__*/React.createElement(SecondaryLabel, {
      $disabled: disabled,
      $secondaryLine: true
    }, secondaryLabel));
  }
  var DecoratedCheckbox = quantityDecorator({
    element: CheckboxComponent,
    secondaryLabel: secondaryLabel,
    labelPosition: labelPosition,
    disabled: disabled
  });
  return checkbox.label || icon ? /*#__PURE__*/React.createElement(Label, {
    $disabled: !!disabled,
    $error: !!error,
    $position: labelPosition,
    $hoverBackground: hoverBackground,
    $isOnHover: isOnHover,
    className: className,
    $padding: padding,
    onClick: function onClick(e) {
      return e.stopPropagation();
    },
    $fullWidth: fullWidth,
    htmlFor: checkbox.id
  }, DecoratedCheckbox, /*#__PURE__*/React.createElement(LabelContent, {
    $fullWidth: fullWidth
  }, checkbox.label && /*#__PURE__*/React.createElement(LabelText, null, checkbox.label), icon && /*#__PURE__*/React.createElement(LabelIcon, null, icon))) : /*#__PURE__*/React.createElement(React.Fragment, null, DecoratedCheckbox);
};
var SecondaryLabelAndCheckbox = /*#__PURE__*/styled.div.withConfig({
  displayName: "Checkbox__SecondaryLabelAndCheckbox",
  componentId: "sc-1p0r96s-0"
})(["display:flex;justify-content:center;align-items:center;flex-wrap:nowrap;flex-direction:", ";"], function (_ref2) {
  var $labelPosition = _ref2.$labelPosition;
  return $labelPosition === 'start' ? 'row' : 'row-reverse';
});
var SecondaryLabel = /*#__PURE__*/styled.span.withConfig({
  displayName: "Checkbox__SecondaryLabel",
  componentId: "sc-1p0r96s-1"
})(["", ";color:", ";", ""], function (_ref3) {
  var theme = _ref3.theme;
  return theme.kallisto.mixins.font('BodySmall');
}, function (_ref4) {
  var theme = _ref4.theme,
    $disabled = _ref4.$disabled;
  return $disabled ? theme.kallisto.color.text.static.textDisabled : theme.kallisto.color.text.static.textSecondary;
}, function (_ref5) {
  var theme = _ref5.theme,
    $secondaryLine = _ref5.$secondaryLine;
  return !$secondaryLine && css(["padding:0 ", ";"], theme.kallisto.space.space100);
});
var SecondaryLabelStack = /*#__PURE__*/styled.div.withConfig({
  displayName: "Checkbox__SecondaryLabelStack",
  componentId: "sc-1p0r96s-2"
})(["display:flex;flex-direction:column;"]);
var SecondaryLabelColumn = /*#__PURE__*/styled.span.withConfig({
  displayName: "Checkbox__SecondaryLabelColumn",
  componentId: "sc-1p0r96s-3"
})(["display:inline-flex;flex-direction:column;align-items:flex-start;margin-left:", ";margin-right:", ";", ""], function (_ref6) {
  var theme = _ref6.theme,
    $position = _ref6.$position;
  return $position !== 'start' ? theme.kallisto.space.space100 : '0';
}, function (_ref7) {
  var theme = _ref7.theme,
    $position = _ref7.$position;
  return $position === 'start' ? theme.kallisto.space.space100 : '0';
}, function (_ref8) {
  var $fullWidth = _ref8.$fullWidth;
  return $fullWidth && css(["flex:1;"]);
});
var quantityDecorator = function quantityDecorator(_ref9) {
  var element = _ref9.element,
    secondaryLabel = _ref9.secondaryLabel,
    labelPosition = _ref9.labelPosition,
    disabled = _ref9.disabled;
  if (secondaryLabel) {
    return /*#__PURE__*/React.createElement(SecondaryLabelAndCheckbox, {
      $labelPosition: labelPosition
    }, /*#__PURE__*/React.createElement(SecondaryLabel, {
      $disabled: disabled
    }, secondaryLabel), element);
  }
  return element;
};
var CheckboxSquare = /*#__PURE__*/styled(RadixCheckbox.Root).withConfig({
  shouldForwardProp: function shouldForwardProp(prop) {
    return !['$error'].includes(prop);
  }
}).withConfig({
  displayName: "Checkbox__CheckboxSquare",
  componentId: "sc-1p0r96s-4"
})(["cursor:pointer;width:18px;height:18px;border:", " solid ", ";background:none;display:inline-flex;flex-shrink:0;align-items:center;justify-content:center;padding:0;transition:border-color 150ms ease-out,background-color 150ms ease-out,box-shadow 150ms ease-out;border-radius:", ";&:hover:not([data-disabled]){border-color:", ";}&:focus-visible{outline:2px ", " solid;outline-offset:0;box-shadow:0 0 0 4px ", ";border-color:", ";}&[data-state='checked'],&[data-state='indeterminate']{background-color:", ";border-color:", ";&:hover:not([data-disabled]){background-color:", ";border-color:", ";}&:focus-visible{background-color:", ";border-color:", ";}&[data-disabled]{background-color:", ";border-color:", ";}}&[data-disabled]{cursor:default;border-color:", ";}", ""], function (_ref0) {
  var theme = _ref0.theme;
  return theme.kallisto.border.width.width12;
}, function (_ref1) {
  var theme = _ref1.theme;
  return theme.kallisto.color.border.interactive.borderNeutralDefault;
}, function (_ref10) {
  var theme = _ref10.theme;
  return theme.kallisto.border.radius.radius50;
}, function (_ref11) {
  var theme = _ref11.theme;
  return theme.kallisto.color.border.interactive.borderNeutralHover;
}, function (_ref12) {
  var theme = _ref12.theme;
  return theme.kallisto.color.common.white[1000];
}, function (_ref13) {
  var theme = _ref13.theme;
  return theme.kallisto.color.common.black[1000];
}, function (_ref14) {
  var theme = _ref14.theme;
  return theme.kallisto.color.border.interactive.borderNeutralHover;
}, function (_ref15) {
  var theme = _ref15.theme;
  return theme.kallisto.color.background.interactive.backgroundBrandContrastDefault;
}, function (_ref16) {
  var theme = _ref16.theme;
  return theme.kallisto.color.background.interactive.backgroundBrandContrastDefault;
}, function (_ref17) {
  var theme = _ref17.theme;
  return theme.kallisto.color.background.interactive.backgroundBrandContrastHover;
}, function (_ref18) {
  var theme = _ref18.theme;
  return theme.kallisto.color.background.interactive.backgroundBrandContrastHover;
}, function (_ref19) {
  var theme = _ref19.theme;
  return theme.kallisto.color.background.interactive.backgroundBrandContrastHover;
}, function (_ref20) {
  var theme = _ref20.theme;
  return theme.kallisto.color.background.interactive.backgroundBrandContrastHover;
}, function (_ref21) {
  var theme = _ref21.theme;
  return theme.kallisto.color.background.static.backgroundDisabled;
}, function (_ref22) {
  var theme = _ref22.theme;
  return theme.kallisto.color.background.static.backgroundDisabled;
}, function (_ref23) {
  var theme = _ref23.theme;
  return theme.kallisto.color.border.static.borderDisabled;
}, function (_ref24) {
  var $error = _ref24.$error,
    theme = _ref24.theme;
  return $error && css(["&&&{border-color:", ";background-color:", ";}&&&:hover:not([data-disabled]){border-color:", ";background-color:", ";}&&&:focus-visible{border-color:", ";background-color:", ";}"], theme.kallisto.color.border.static.borderErrorContrast, theme.kallisto.color.background.static.backgroundErrorSubtle, theme.kallisto.color.border.static.borderErrorContrast, theme.kallisto.color.background.static.backgroundErrorSubtle, theme.kallisto.color.border.static.borderErrorContrast, theme.kallisto.color.background.static.backgroundLayer02);
});
var CheckboxIndicator = /*#__PURE__*/styled(RadixCheckbox.Indicator).withConfig({
  displayName: "Checkbox__CheckboxIndicator",
  componentId: "sc-1p0r96s-5"
})(["line-height:0;display:flex;align-items:center;justify-content:center;"]);
var IndeterminateDash = /*#__PURE__*/styled.span.withConfig({
  displayName: "Checkbox__IndeterminateDash",
  componentId: "sc-1p0r96s-6"
})(["width:10px;height:2px;background-color:", ";border-radius:1px;"], function (_ref25) {
  var $color = _ref25.$color;
  return $color;
});
var LabelIcon = /*#__PURE__*/styled.span.withConfig({
  displayName: "Checkbox__LabelIcon",
  componentId: "sc-1p0r96s-7"
})(["display:inline-flex;align-items:center;flex-shrink:0;"]);
var LabelContent = /*#__PURE__*/styled.span.withConfig({
  displayName: "Checkbox__LabelContent",
  componentId: "sc-1p0r96s-8"
})(["display:inline-flex;align-items:center;gap:", ";", ""], function (_ref26) {
  var theme = _ref26.theme;
  return theme.kallisto.space.space50;
}, function (_ref27) {
  var $fullWidth = _ref27.$fullWidth;
  return $fullWidth && css(["flex:1;justify-content:space-between;"]);
});
var LabelText = /*#__PURE__*/styled.div.withConfig({
  displayName: "Checkbox__LabelText",
  componentId: "sc-1p0r96s-9"
})(["", ";"], function (_ref28) {
  var theme = _ref28.theme;
  return theme.kallisto.mixins.font('Body');
});
var Label = /*#__PURE__*/styled.label.withConfig({
  displayName: "Checkbox__Label",
  componentId: "sc-1p0r96s-10"
})(["cursor:pointer;display:inline-flex;align-items:center;justify-content:space-between;border-radius:", ";transition:background-color 150ms ease-out;padding:", ";width:", ";", " ", " ", " ", " ", ""], function (_ref29) {
  var theme = _ref29.theme;
  return theme.kallisto.border.radius.radius50;
}, function (_ref30) {
  var theme = _ref30.theme,
    $padding = _ref30.$padding;
  return $padding ? theme.mixins.padding($padding) : "".concat(theme.kallisto.space.space100, " ").concat(theme.kallisto.space.space50);
}, function (_ref31) {
  var $fullWidth = _ref31.$fullWidth;
  return $fullWidth ? '100%' : 'auto';
}, function (_ref32) {
  var $error = _ref32.$error,
    theme = _ref32.theme;
  return !$error && css(["&:hover ", ":not([data-disabled]){border-color:", ";&[data-state='checked'],&[data-state='indeterminate']{background-color:", ";border-color:", ";}}"], CheckboxSquare, theme.kallisto.color.border.interactive.borderNeutralHover, theme.kallisto.color.background.interactive.backgroundBrandContrastHover, theme.kallisto.color.background.interactive.backgroundBrandContrastHover);
}, function (_ref33) {
  var $isOnHover = _ref33.$isOnHover,
    $hoverBackground = _ref33.$hoverBackground,
    $error = _ref33.$error,
    theme = _ref33.theme;
  return $isOnHover && !$error ? css(["", ":not([data-disabled]){border-color:", ";&[data-state='checked'],&[data-state='indeterminate']{background-color:", ";border-color:", ";}}", ""], CheckboxSquare, theme.kallisto.color.border.interactive.borderNeutralHover, theme.kallisto.color.background.interactive.backgroundBrandContrastHover, theme.kallisto.color.background.interactive.backgroundBrandContrastHover, $hoverBackground ? css(["background-color:", ";"], theme.kallisto.color.background.interactive.backgroundNeutralHover) : '') : '';
}, function (_ref34) {
  var $disabled = _ref34.$disabled,
    theme = _ref34.theme;
  return $disabled ? css(["color:", ";cursor:default;&:hover ", "{border-color:", ";&[data-state='checked'],&[data-state='indeterminate']{background-color:", ";border-color:", ";}}"], theme.kallisto.color.text.static.textDisabled, CheckboxSquare, theme.kallisto.color.border.static.borderDisabled, theme.kallisto.color.background.static.backgroundDisabled, theme.kallisto.color.background.static.backgroundDisabled) : '';
}, function (_ref35) {
  var $position = _ref35.$position,
    theme = _ref35.theme;
  return $position === 'start' ? css(["flex-direction:row-reverse;", "{margin-right:", ";}"], LabelContent, theme.kallisto.space.space100) : css(["", "{margin-left:", ";}"], LabelContent, theme.kallisto.space.space100);
}, function (_ref36) {
  var $hoverBackground = _ref36.$hoverBackground,
    theme = _ref36.theme;
  return $hoverBackground ? css(["&:hover{background-color:", ";}"], theme.kallisto.color.background.interactive.backgroundNeutralHover) : '';
});
export var CheckboxStyles = {
  CheckboxSquare: CheckboxSquare,
  LabelText: LabelText,
  Label: Label,
  SecondaryLabel: SecondaryLabel,
  SecondaryLabelStack: SecondaryLabelStack,
  SecondaryLabelColumn: SecondaryLabelColumn
};